<template>
  <div class="c-client-details">
    <v-dialog
      v-model="showDialog"
      max-width='900'
    >
      <v-card v-if="client">
        <v-card-title>
          Client details
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form
            v-model="valid"
            lazy-validation
          >
            <v-row v-if="deleteMode">
              <v-col cols="12">
                <v-textarea
                  label="Deleted note"
                  v-model="copyOfClient.Deleted_Notes"
                  counter="100"
                  :rules="rules100"
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols='12' md="4" lg="4" class="d-flex flex-column justify-space-between">
                <!-- Name -->
                <div>
                  <v-text-field
                    label="Name"
                    v-model="copyOfClient.Name"
                    :readonly="!editMode"
                    counter="50"
                    :rules="editMode ? rules50: []"
                  >
                  </v-text-field>
                </div>
                <!-- /Name -->
                <!-- Address -->
                <div>
                  <v-textarea
                    label="Address"
                    v-model="copyOfClient.Address"
                    :readonly="!editMode"
                    :rows="4"
                    :rules="editMode ? rules50: []"
                    counter="50"
                  >
                  </v-textarea>
                </div>
                <!-- /Address -->
                <!-- Website -->
                <div>
                  <v-text-field
                    label="Website"
                    v-model="copyOfClient.Website"
                    :readonly="!editMode"
                    :rules="editMode ? rules100: []"
                    counter="100"
                  >
                  </v-text-field>
                </div>
                <!-- /Website -->
                <!-- Payment Method -->
                <div>
                  <v-text-field
                    label="Payment Method"
                    v-model="copyOfClient.Payment_Method"
                    :readonly="!editMode"
                    :rules="editMode ? rules150: []"
                    counter="150"
                  >
                  </v-text-field>
                </div>
                <!-- /Payment Method -->
                <!-- Notes -->
                <div>
                  <v-textarea
                    label="Notes"
                    v-model="copyOfClient.Notes"
                    hide-details
                    :readonly="!editMode"
                    :rows="4"
                    counter="500"
                  >
                  </v-textarea>
                </div>
                <!-- /Notes -->
                <!-- Status -->
                <div>
                  <v-switch
                    v-model="copyOfClient.Status"
                    hide-details
                    label="Active"
                    :readonly="!editMode"
                  ></v-switch>
                </div>
                <!-- /Status -->
              </v-col>
              <v-col cols="12" md="8" lg="8">
                <v-data-table
                  dense
                  class="mt-4"
                  :headers="contact_headers"
                  :items="client_contacts"
                  hide-default-footer
                  :items-per-page="-1"
                >
                <template v-slot:[`item.Contact_Name`]='{ item }'>
                  <h3>{{item.Contact_Name}}</h3>
                  {{item.Contact_Position}}
                </template>
                <template v-slot:[`item.Contact_Info`]='{ item }'>
                  <div>
                    <span
                      @click.stop="copyToClipboard(item.Contact_Phone, 'Phone')"
                      class="cursor-pointer"
                      color="primary"
                    >
                      {{ item.Contact_Phone }}
                    </span>
                    <span
                      @click.stop="copyToClipboard(item.Contact_Email, 'Email')"
                      class="cursor-pointer"
                      color="primary"
                    >
                      {{ item.Contact_Email }}
                    </span>
                  </div>
                </template>
                <template v-slot:[`item.Actions`]='{ item, index }'>
                  <v-btn
                    small
                    icon
                    :disabled="!editMode"
                    color="primary"
                    @click="editContactRow(item)"
                  >
                    <v-icon>mdi-pencil-circle</v-icon>
                  </v-btn>
                  <v-btn
                    small
                    icon
                    :disabled="!editMode"
                    color="red darken-2"
                    @click="deleteContactRow(item, index)"
                  >
                    <v-icon>mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                </v-data-table>
                <v-btn
                  v-if="editMode || !client.Client_ID"
                  color="success"
                  class="mt-2"
                  small
                  @click.prevent="addNewContact"
                >
                  Add Contact
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider v-if="canEdit && !notFromClientsModule"></v-divider>
        <v-card-actions v-if="canEdit && !notFromClientsModule">
          <v-btn small outlined color="primary" v-if="copyOfClient.Client_ID && !editMode && !copyOfClient.Is_Deleted" @click="projectsDialog = true">Projects</v-btn>
          <v-btn small outlined color="primary" v-if="copyOfClient.Client_ID && !editMode && !copyOfClient.Is_Deleted" @click="goToGeneralReportsPage">Reports</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            :color="copyOfClient.Is_Deleted ? 'primary' : 'red darken-2 white--text'"
            v-if="canEdit && !deleteMode && !editMode"
            @click.prevent="toggleDeleteMode"
          >
            {{ copyOfClient.Is_Deleted ? 'Restore' : 'Delete'}}
          </v-btn>
          <v-btn
            small
            color="primary"
            v-if="!editMode && !deleteMode && !copyOfClient.Is_Deleted"
            @click.prevent="turnEditModeOn"
          >
            Edit
          </v-btn>
          <v-btn small outlined color="primary" v-if="editMode || deleteMode" @click.prevent="cancelChanges">Cancel</v-btn>
          <v-btn
            small
            :color="deleteMode ? 'red darken-2 white--text' : 'primary'"
            v-if="(editMode || deleteMode)"
            :disabled="valid && !canSave && editMode"
            @click.prevent="saveChanges"
          >
            {{ deleteMode ? 'Delete' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      scrollable
      v-model="projectsDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Projects
          <v-spacer></v-spacer>
          <v-text-field
            dense
            hide-details
            class="mr-3"
            label="Search Project"
            v-model="projectSearch"
            append-icon="mdi-magnify"
          ></v-text-field>
          <v-btn
            icon
            @click.prevent="projectsDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <v-data-table
            dense
            :headers="projectsHeader"
            :items="projectsList"
            :items-per-page="-1"
            :search="projectSearch"
            hide-default-footer
          >
            <template v-slot:[`item.Actions`]="{ item }">
              <v-btn
                small
                text
                plain
                color="primary"
                class="my-2"
                @click="showProjectInfo(item.Project_ID)"
              >
                details...
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      scrollable
      v-model="contactDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Contact
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="contactDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <h3 class="mt-1">Contact Name</h3>
              <v-text-field
                v-model="client_Contact.Contact_Name"
              ></v-text-field>
              <h3>Position / Role</h3>
              <v-text-field
                v-model="client_Contact.Contact_Position"
              ></v-text-field>
              <h3>Email</h3>
              <v-text-field
                v-model="client_Contact.Contact_Email"
                :rules="editMode ? emailRules: []"
              ></v-text-field>
              <h3>Phone</h3>
              <v-text-field
                v-model="client_Contact.Contact_Phone"
              ></v-text-field>
              <h3>Notes</h3>
              <v-textarea
                v-model="client_Contact.Contact_Notes"
                rows="4"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            outlined
            small
            color="primary"
            @click.prevent="cancelContactEditing"
          >
            Cancel
          </v-btn>
          <v-btn
            small
            :disabled="!canSaveContact"
            color="primary"
            @click.prevent="updateClientContact"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="contactDeleteDialog"
      v-model="contactDeleteDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteClientContact"
      @onCancelClick="contactDeleteDialog = false; contactRemoveId = null;"
    />
  </div>
</template>
<script>
export default {
  name: 'ClientDetails',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    client: {
      required: false,
      default: null
    },
    notFromClientsModule: {
      required: false,
      default: false
    },
    editClient: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      valid: true,
      projectsDialog: false,
      projectSearch: '',
      edit: false,
      deleteMode: false,
      copyOfClient: {},
      projectsList: [],
      rolesOfCurrentUser: [],
      client_contacts: [],
      client_Contact: {
        Client_ID: null,
        Contact_ID: null,
        Contact_Name: '',
        Contact_Position: '',
        Contact_Phone: '',
        Contact_Email: '',
        Contact_Notes: ''
      },
      contactDialog: false,
      contactRemoveId: null,
      contactDeleteDialog: false,
      editContactMode: false,
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid'
      ],
      rules50: [
        v => !v || v.length <= 50 || 'Max 50 characters'
      ],
      rules100: [
        v => v.length <= 100 || 'Max 100 characters'
      ],
      rules150: [
        v => v.length <= 150 || 'Max 150 characters'
      ],
      projectsHeader: [
        {
          text: 'Project Name',
          value: 'Description',
          divider: true,
          class: 'text-no-wrap'
        },
        {
          text: 'Project Status',
          value: 'Status_Description',
          divider: true,
          class: 'text-no-wrap'
        },
        {
          text: '',
          align: 'center',
          value: 'Actions',
          class: 'text-no-wrap'
        }
      ],
      contact_headers: [
        {
          text: 'Contact name',
          value: 'Contact_Name',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Phone / Email',
          value: 'Contact_Info',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Contact_Notes',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: '',
          value: 'Actions',
          sortable: false,
          width: '90px',
          class: 'px-2 text-no-wrap'
        }
      ]
    }
  },
  async mounted () {
    await this.getRolesOfCurrentUser()
    this.copyOfClient = Object.assign({}, this.client)
    if (this.client.Client_ID) {
      this.getClientContacts()
      this.getProjectsByClient()
      if (this.editClient) this.editMode = true
    }
  },
  computed: {
    canEdit () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['manager', 'financier', 'hr'].indexOf(this.rolesOfCurrentUser[i].RoleName.toLowerCase()) > -1) {
          res = true
          break
        }
      }
      return res
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    user () {
      return this.$store.getters.getUser
    },
    projects () {
      return this.$store.getters.getProjects
    },
    editMode: {
      get () {
        return !this.client.Client_ID || this.edit
      },
      set (val) {
        this.edit = val
      }
    },
    showDialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    infoWasChanged () {
      let res = false
      for (const key in this.copyOfClient) {
        if (this.copyOfClient[key] === this.client[key]) continue
        res = true
        break
      }
      return res
    },
    canSave () {
      return this.copyOfClient.Name && this.infoWasChanged
    },
    canSaveContact () {
      return this.client_Contact.Contact_Name || this.client_Contact.Contact_Position || this.client_Contact.Contact_Email || this.client_Contact.Contact_Phone
    }
  },
  methods: {
    async getRolesOfCurrentUser () {
      await this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    openProjectCard (projectID) {
      const proj = this.projects.filter(p => p.Project_ID === projectID)[0]
      if (proj) {
        proj.Get_Date = proj.Get_Date ? new Date(proj.Get_Date).toISOString().substr(0, 10) : null
        proj.Start_Date = proj.Start_Date ? new Date(proj.Start_Date).toISOString().substr(0, 10) : null
        proj.Release_Date = proj.Release_Date ? new Date(proj.Release_Date).toISOString().substr(0, 10) : null
        this.$store.dispatch('projectDetailsScreen', {
          show: true,
          project: proj
        })
      } else {
        this.$store.dispatch('setError', { errorMsg: 'Something went wrong!' })
      }
    },
    getProjectsByClient () {
      this.axios.get(`/api/projects_by_client_id/${this.client.Client_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.projectsList = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    toggleDeleteMode () {
      if (this.copyOfClient.Is_Deleted) {
        this.copyOfClient.Is_Deleted = false
        this.copyOfClient.Deleted_Notes = ''
        this.saveChanges()
      } else {
        this.deleteMode = true
        const date = new Date().toLocaleString()
        this.copyOfClient.Is_Deleted = true
        this.copyOfClient.Deleted_Notes = `Deleted by: ${this.user.login}, Date: ${date}`
      }
    },
    turnEditModeOn () {
      this.editMode = true
    },
    cancelChanges () {
      this.editMode = false
      this.deleteMode = false
      this.copyOfClient = Object.assign({}, this.client)
      if (!this.copyOfClient.Client_ID) {
        this.showDialog = false
      }
    },
    saveChanges () {
      this.editMode = false
      this.deleteMode = false
      if (!this.copyOfClient.Client_ID) {
        this.copyOfClient.Contacts = this.client_contacts
      }
      this.$emit(this.copyOfClient.Client_ID ? 'updateClient' : 'createClient', this.copyOfClient)
    },
    showProjectInfo (projectID) {
      const proj = this.projects.filter(p => p.Project_ID === projectID)[0]
      if (proj) {
        proj.Get_Date = proj.Get_Date ? new Date(proj.Get_Date).toISOString().substr(0, 10) : null
        proj.Start_Date = proj.Start_Date ? new Date(proj.Start_Date).toISOString().substr(0, 10) : null
        proj.Release_Date = proj.Release_Date ? new Date(proj.Release_Date).toISOString().substr(0, 10) : null
        this.$store.dispatch('projectDetailsScreen', {
          show: true,
          project: proj
        })
      } else {
        this.$store.dispatch('setError', { errorMsg: 'Project does not found!' })
      }
    },
    getClientContacts () {
      this.axios.get(`/api/client/contacts/${this.client.Client_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.client_contacts = result.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    addNewContact () {
      this.clearClientContact()
      this.contactDialog = true
    },
    updateClientContact () {
      if (this.editContactMode) {
        this.editClientContact()
      } else {
        this.createClientContact()
      }
    },
    createClientContact () {
      if (this.client.Client_ID) {
        this.client_Contact.Client_ID = this.client.Client_ID
        this.axios.post('/api/client/contacts', this.client_Contact, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(result => {
          this.client_Contact.Contact_ID = result.data[0].Contact_ID
          this.client_contacts.push({ ...this.client_Contact })
          this.contactDialog = false
          this.$store.dispatch('setSuccess', { successMsg: 'Client contact added' })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      } else {
        const newItem = { ...this.client_Contact }
        this.client_contacts.push(newItem)
        this.contactDialog = false
      }
    },
    editContactRow (item) {
      this.client_Contact = item
      this.contactDialog = true
      this.editContactMode = true
    },
    editClientContact () {
      if (this.client.Client_ID) {
        this.axios.put(`/api/client/contacts/${this.client_Contact.Contact_ID}`, this.client_Contact, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(result => {
          this.clearClientContact()
          this.contactDialog = false
          this.getClientContacts()
          this.$store.dispatch('setSuccess', { successMsg: 'Client contact updated' })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      } else {
        this.client_Contact = { ...this.client_Contact }
        this.contactDialog = false
        this.editContactMode = false
      }
    },
    deleteContactRow (item, index) {
      if (this.client.Client_ID) {
        this.contactRemoveId = item.Contact_ID
      } else {
        this.contactRemoveId = index
      }
      this.contactDeleteDialog = true
    },
    deleteClientContact () {
      if (this.client.Client_ID) {
        this.axios.delete(`/api/client/contacts/${this.contactRemoveId}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(result => {
          this.clearClientContact()
          this.getClientContacts()
          this.contactDeleteDialog = false
          this.$store.dispatch('setSuccess', { successMsg: 'Client contact deleted' })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      } else {
        this.client_contacts.splice(this.contactRemoveId, 1)
        this.contactDeleteDialog = false
      }
    },
    cancelContactEditing () {
      this.contactDialog = false
      this.editContactMode = false
      this.getClientContacts()
    },
    clearClientContact () {
      this.client_Contact.Client_ID = null
      this.client_Contact.Contact_ID = null
      this.client_Contact.Contact_Name = ''
      this.client_Contact.Contact_Position = ''
      this.client_Contact.Contact_Phone = ''
      this.client_Contact.Contact_Email = ''
      this.client_Contact.Contact_Notes = ''
    },
    copyToClipboard (content, target) {
      navigator.clipboard.writeText(content).then(() => {
        this.$store.dispatch('setSuccess', { successMsg: `${target} was copied` })
      }, () => {
        this.$store.dispatch('setError', { errorMsg: `${target} wasn't copied` })
      })
    },
    goToGeneralReportsPage () {
      this.$router.push({
        name: 'ManagerReports',
        params: {
          Client_ID: this.client.Client_ID
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
  .color-primary {
    color:#1976d2;
  }
  .cursor-pointer {
    cursor: pointer;
    text-decoration: underline;
    color: #1976d2;
    font-size: 1.17em;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }
</style>
